import Link from "next/link";
import { useAtomValue } from "jotai";
import { userState } from "@/lib/store";
import type { ShortList } from "@/lib/short-lists";
import styles from "./ShortListTable.module.scss";

const pct = (value: number | null, digits = 1) => (value === null ? "–" : `${value.toLocaleString("en-US", { maximumFractionDigits: digits, minimumFractionDigits: digits })}%`);
const days = (value: number | null) => (value === null ? "–" : value.toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 }));

interface Props {
    list: ShortList | null | undefined;
    /** The table's H2. */
    heading: string;
    /** One line under the heading saying how the list is ranked. */
    criteria: string;
    /** Where the full list is, for the line under the table. */
    fullListHref: string;
    fullListLabel: string;
    showDaysToCover?: boolean;
}

/**
 * A server-rendered short-interest list (src/lib/short-lists.ts): ticker linked to its stock page,
 * company and the figures, below the page. It draws only the rows it was given, which the page got
 * from `getStaticProps`, so the browser hydrates the same rows and never fetches or swaps them.
 *
 * Visitors only. A signed-in reader has the full, live list in the page's own grid, so the table is
 * dropped as soon as a signed-in user is known; it sits below the viewport-high page, so that never
 * moves anything a visitor is looking at.
 */
const ShortListTable = ({ list, heading, criteria, fullListHref, fullListLabel, showDaysToCover = false }: Props) => {
    const user = useAtomValue(userState);
    const signedIn = !!user?.subscription_plan && user.subscription_plan !== "ORTEX Visitor";
    if (!list?.rows.length || signedIn) return null;

    return (
        <section className={styles.ShortListTable} aria-labelledby="short-list-heading">
            <h2 id="short-list-heading">{heading}</h2>
            <p className={styles.criteria}>
                {criteria}
                {list.asOf ? ` Figures as of ${list.asOf}.` : ""}
            </p>
            <table>
                <thead>
                    <tr>
                        <th scope="col">Ticker</th>
                        <th scope="col">Company</th>
                        <th scope="col">Short interest (% of free float)</th>
                        {showDaysToCover && <th scope="col">Days to cover</th>}
                        <th scope="col">Cost to borrow</th>
                    </tr>
                </thead>
                <tbody>
                    {list.rows.map((row) => (
                        <tr key={row.href}>
                            <td>
                                <Link href={row.href} prefetch={false}>
                                    {row.ticker}
                                </Link>
                            </td>
                            <td>{row.name}</td>
                            <td>{pct(row.siPercentFf)}</td>
                            {showDaysToCover && <td>{days(row.daysToCover)}</td>}
                            <td>{pct(row.costToBorrow, 2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className={styles.more}>
                {list.total && list.total > list.rows.length ? `The top ${list.rows.length} of ${list.total.toLocaleString("en-US")} stocks. ` : ""}
                <Link href={fullListHref} prefetch={false}>
                    {fullListLabel}
                </Link>
            </p>
        </section>
    );
};

export default ShortListTable;

/** The two pages that carry a list, and how each is described. */
export const SHORT_LIST_VIEWS = {
    mostShorted: {
        heading: "Most shorted stocks today",
        criteria: "US stocks with a market value of $500 million or more, ranked by ORTEX's estimate of short interest as a percentage of free float.",
        fullListHref: "/short-interest?tab=live_short_screener",
        fullListLabel: "See the full ranked list in the Live Short Screener",
        showDaysToCover: false,
    },
    squeeze: {
        heading: "Top short squeeze candidates",
        criteria: "US stocks with a market value of $500 million or more, ranked by the ORTEX Short Score (short interest as % of free float, cost to borrow, utilization and days to cover).",
        fullListHref: "/short-interest?tab=short_extremes",
        fullListLabel: "See the full ranked list in Short Extremes",
        showDaysToCover: true,
    },
} as const;

export type ShortListView = keyof typeof SHORT_LIST_VIEWS;

export const ShortListBlock = ({ list, view }: { list: ShortList | null | undefined; view: ShortListView }) => <ShortListTable list={list} {...SHORT_LIST_VIEWS[view]} />;
